import React from 'react';
import { Edit2, Trash2, Bookmark, Globe, User, Calendar, Shield, Tag, MoreVertical, Eye, Terminal } from 'lucide-react';
import { Table, Column, SortDirection, TableCell, TableRow } from '@/components/common/ui/Table'; // Note: updated to use named imports for Table components
import { ThreatIntelRecordDetail, SeverityType, StatusType } from '@/lib/api/types';
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator
} from '@/components/common/ui/DropdownMenu';

// Neon Badge Components
interface SeverityBadgeProps {
    severity: SeverityType;
}

interface StatusBadgeProps {
    status: StatusType;
}

const SeverityBadge: React.FC<SeverityBadgeProps> = ({ severity }) => {
    const getConfig = (severity: SeverityType) => {
        switch (severity) {
            case 'critical':
                return 'bg-[#FF3333]/10 text-[#FF3333] border-[#FF3333]/30';
            case 'high':
                return 'bg-[#F97316]/10 text-[#F97316] border-[#F97316]/30';
            case 'medium':
                return 'bg-[#EAB308]/10 text-[#EAB308] border-[#EAB308]/30';
            case 'low':
                return 'bg-[#50A970]/10 text-[#50A970] border-[#50A970]/30';
            default:
                return 'bg-slate-500/10 text-slate-500 border-slate-500/30';
        }
    };

    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[9px] font-bold uppercase tracking-widest border ${getConfig(severity)}`}>
            <Shield className="h-3 w-3" />
            {severity}
        </span>
    );
};

const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => {
    const getConfig = (status: StatusType) => {
        switch (status) {
            case 'new':
                return 'bg-[#00FFFF]/10 text-[#00FFFF] border-[#00FFFF]/30';
            case 'investigating':
                return 'bg-[#EAB308]/10 text-[#EAB308] border-[#EAB308]/30';
            case 'resolved':
                return 'bg-[#50A970]/10 text-[#50A970] border-[#50A970]/30';
            case 'false_positive':
                return 'bg-slate-500/10 text-slate-400 border-slate-500/30';
            default:
                return 'bg-slate-500/10 text-slate-400 border-slate-500/30';
        }
    };

    const displayText = status === 'false_positive' ? 'False Positive' : status;

    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[9px] font-bold uppercase tracking-widest border ${getConfig(status)}`}>
            <Tag className="h-3 w-3" />
            {displayText}
        </span>
    );
};

// Cyber URL Component
const URLDisplay: React.FC<{ url?: string | null; hostname?: string | null }> = ({ url, hostname }) => {
    const displayUrl = url || hostname || '-';

    if (displayUrl === '-') {
        return <span className="text-slate-600 font-mono pl-4">-</span>;
    }

    const truncateUrl = (url: string, maxLength: number = 30) => {
        if (url.length <= maxLength) return url;
        return url.substring(0, maxLength) + '...';
    };

    return (
        <div className="flex items-center gap-2 min-w-0 max-w-xs group cursor-default">
            <Globe className="h-3.5 w-3.5 text-slate-500 group-hover:text-[#00FFFF] transition-colors flex-shrink-0" />
            <span
                className="font-mono text-[12px] text-slate-300 group-hover:text-white transition-colors truncate"
                title={displayUrl}
            >
                {truncateUrl(displayUrl)}
            </span>
        </div>
    );
};

// Cyber Username Component
const UsernameDisplay: React.FC<{ username?: string | null }> = ({ username }) => {
    if (!username) {
        return <span className="text-slate-600 font-mono pl-4">-</span>;
    }

    const truncateUsername = (username: string, maxLength: number = 20) => {
        if (username.length <= maxLength) return username;
        return username.substring(0, maxLength) + '...';
    };

    return (
        <div className="flex items-center gap-2 group cursor-default">
            <User className="h-3.5 w-3.5 text-slate-500 group-hover:text-purple-400 transition-colors flex-shrink-0" />
            <span
                className="font-mono text-[12px] text-slate-300 group-hover:text-white transition-colors truncate"
                title={username}
            >
                {truncateUsername(username)}
            </span>
        </div>
    );
};

// Cyber Date Component
const DateDisplay: React.FC<{ date: string }> = ({ date }) => {
    return (
        <div className="flex items-center gap-2 cursor-default">
            <Calendar className="h-3.5 w-3.5 text-slate-500 flex-shrink-0" />
            <span className="font-mono text-[11px] text-slate-400 uppercase tracking-wider">
                {new Date(date).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })}
            </span>
        </div>
    );
};

type SortField = 'saved_at' | 'severity' | 'status' | 'data_type' | 'url' | 'username' | 'updated_at';

interface ThreatListProps {
    items: ThreatIntelRecordDetail[];
    isLoading: boolean;
    selectedItems: number[];
    sortField: SortField;
    sortDirection: SortDirection;
    onSort: (column: string, direction: SortDirection) => void;
    onSelectAll: (checked: boolean) => void;
    onSelectItem: (itemId: number, checked: boolean) => void;
    onViewDetails: (item: ThreatIntelRecordDetail) => void;
    onEditItem: (item: ThreatIntelRecordDetail) => void;
    onDeleteItem: (id: number) => void;
    itemsPerPage?: number;
}

const ThreatList: React.FC<ThreatListProps> = ({
    items,
    isLoading,
    selectedItems,
    sortField,
    sortDirection,
    onSort,
    onSelectAll,
    onSelectItem,
    onViewDetails,
    onEditItem,
    onDeleteItem,
    itemsPerPage = 25
}) => {
    // Simplified columns - removed data_type and source, made table more compact
    const columns: Column[] = [
        { id: 'severity', label: 'Severity', sortable: true, width: '120px' },
        { id: 'status', label: 'Status', sortable: true, width: '120px' },
        { id: 'url', label: 'URL/Domain', sortable: true, minWidth: '200px' },
        { id: 'username', label: 'Username', sortable: true, width: '160px' },
        { id: 'saved_at', label: 'Saved', sortable: true, width: '140px' },
        { id: 'actions', label: 'Actions', width: '100px' }
    ];

    const getRowVariant = (item: ThreatIntelRecordDetail) => {
        if (item.severity === 'critical') return 'critical';
        if (item.severity === 'high') return 'danger';
        if (item.severity === 'medium') return 'warning';
        return 'default';
    };

    const allRowsSelected = items.length > 0 && selectedItems.length === items.length;
    const indeterminate = selectedItems.length > 0 && selectedItems.length < items.length;

    // Cyber Empty State
    if (!isLoading && items.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-16 px-4 bg-[#0A0B0E] border border-[#1A1D24] rounded-xl shadow-[0_0_30px_rgba(0,0,0,0.5)] font-mono">
                <div className="w-16 h-16 bg-[#12151C] border border-[#1A1D24] rounded-full flex items-center justify-center mb-6 shadow-inner">
                    <Bookmark className="h-6 w-6 text-slate-500" />
                </div>
                <div className="text-center space-y-3 max-w-md">
                    <h3 className="text-[14px] font-bold text-white tracking-widest uppercase flex items-center justify-center gap-2">
                        <Terminal className="h-4 w-4 text-[#00FFFF]" />
                        Database Query Null
                    </h3>
                    <p className="text-[11px] text-slate-400 leading-relaxed uppercase tracking-wider">
                        No persistent threat vectors match current telemetry parameters. Adjust filter nodes or intercept new targets to populate matrix.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="overflow-x-auto custom-scrollbar">
            <Table
                columns={columns}
                onSort={onSort}
                sortColumn={sortField}
                sortDirection={sortDirection}
                selectable
                onSelectAll={onSelectAll}
                allRowsSelected={allRowsSelected}
                indeterminate={indeterminate}
                loading={isLoading}
                loadingRows={itemsPerPage}
            >
                {items.map((item) => (
                    <TableRow
                        key={item.id}
                        selected={selectedItems.includes(item.id)}
                        onSelect={(checked) => onSelectItem(item.id, checked)}
                        rowId={item.id.toString()}
                        variant={getRowVariant(item)}
                        disabled={isLoading}
                    >
                        <TableCell>
                            <SeverityBadge severity={item.severity} />
                        </TableCell>
                        <TableCell>
                            <StatusBadge status={item.status} />
                        </TableCell>
                        <TableCell>
                            <URLDisplay url={item.url} hostname={item.hostname} />
                        </TableCell>
                        <TableCell>
                            <UsernameDisplay username={item.username} />
                        </TableCell>
                        <TableCell>
                            <DateDisplay date={item.saved_at} />
                        </TableCell>
                        <TableCell>
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => onViewDetails(item)}
                                    className="p-1.5 h-auto text-slate-500 hover:text-[#00FFFF] hover:bg-[#161A23] rounded transition-all outline-none"
                                    title="Examine Vector"
                                >
                                    <Eye className="h-4 w-4" />
                                </button>
                                <DropdownMenu>
                                    <DropdownMenuTrigger className="text-slate-500 hover:text-white hover:bg-[#161A23] p-1.5 rounded transition-all outline-none">
                                        <MoreVertical className="h-4 w-4" />
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="cyber-dropdown-menu">
                                        <DropdownMenuItem onClick={() => onEditItem(item)} className="group">
                                            <Edit2 className="h-3.5 w-3.5 mr-2 text-slate-500 group-hover:text-[#00FFFF] transition-colors" />
                                            Edit
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                            onClick={() => onDeleteItem(item.id)}
                                            className="group"
                                            destructive
                                        >
                                            <Trash2 className="h-3.5 w-3.5 mr-2 text-slate-500 group-hover:text-[#FF3333] transition-colors" />
                                            Delete
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                        </TableCell>
                    </TableRow>
                ))}
            </Table>

            {/* ONLY KEEP DROPDOWN INJECTION (Table styles are now handled globally in Table.tsx) */}
            <style dangerouslySetInnerHTML={{
                __html: `
                /* OVERRIDE GLOBAL PORTAL MENUS FOR DROPDOWN */
                [data-radix-popper-content-wrapper] > div[role="menu"],
                .cyber-dropdown-menu,
                div[role="menu"] {
                    background-color: #0A0B0E !important;
                    border: 1px solid #1A1D24 !important;
                    font-family: monospace !important;
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.9) !important;
                    border-radius: 6px !important;
                    overflow: hidden !important;
                    z-index: 99999 !important;
                    min-width: 160px !important;
                    padding: 6px !important;
                }

                div[role="menuitem"] {
                    color: #94a3b8 !important;
                    font-size: 11px !important;
                    text-transform: uppercase !important;
                    letter-spacing: 0.05em !important;
                    padding: 10px 14px !important;
                    cursor: pointer !important;
                    transition: all 0.1s ease !important;
                    border-radius: 4px !important;
                    display: flex !important;
                    align-items: center !important;
                    outline: none !important;
                }

                /* Standard Hover */
                div[role="menuitem"]:hover,
                div[role="menuitem"][data-highlighted] {
                    background-color: #161A23 !important;
                    color: #00FFFF !important;
                }

                /* Destructive Menu Item Hover (Purge Record) */
                div[role="menuitem"][data-destructive="true"]:hover,
                div[role="menuitem"]:last-child:hover {
                    background-color: #FF333310 !important;
                    color: #FF3333 !important;
                }

                div[role="separator"] {
                    background-color: #1A1D24 !important;
                    height: 1px !important;
                    margin: 4px 0 !important;
                }
            `}} />
        </div>
    );
};

export default ThreatList;